import type {
  AgeRange,
  Anatomy,
  AnswerValue,
  CreateSessionResponse,
  CreateCheckoutResponse,
  MatchResult,
} from '../types';
import { buildFunctionHeaders } from './functionHeaders';
import { recordApiDuration, recordApiError } from './sentryTelemetry';

const env = (
  import.meta as ImportMeta & { env?: Record<string, string | undefined> }
).env ?? {};

const FUNCTIONS_URL = `${env.VITE_SUPABASE_URL ?? ''}/functions/v1`;
const NETWORK_ERROR_STATUS = 0;

function getNowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
}

async function callFunction<T>(
  name: string,
  options: {
    method?: string;
    body?: unknown;
    jwt?: string;
    partnerToken?: string;
    queryParams?: Record<string, string>;
  } = {},
): Promise<T> {
  const { method = 'POST', body, jwt, partnerToken, queryParams } = options;
  const endpoint = name.split('?')[0] ?? name;
  const startedAtMs = getNowMs();

  let url = `${FUNCTIONS_URL}/${name}`;
  if (queryParams) {
    const params = new URLSearchParams(queryParams);
    url += `?${params.toString()}`;
  }

  const headers = buildFunctionHeaders({
    apikey: env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '',
    jwt,
    partnerToken,
  });

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json();
    const durationMs = getNowMs() - startedAtMs;

    if (!res.ok) {
      const message = data.error || 'Request failed';
      recordApiDuration('api.duration', durationMs, {
        endpoint,
        method,
        status: res.status,
        ok: false,
      });
      recordApiError(endpoint, res.status, message, method);
      throw new ApiError(message, res.status, data);
    }

    recordApiDuration('api.duration', durationMs, {
      endpoint,
      method,
      status: res.status,
      ok: true,
    });
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    const durationMs = getNowMs() - startedAtMs;
    const message = error instanceof Error ? error.message : 'Request failed';

    recordApiDuration('api.duration', durationMs, {
      endpoint,
      method,
      status: NETWORK_ERROR_STATUS,
      ok: false,
    });
    recordApiError(endpoint, NETWORK_ERROR_STATUS, message, method);

    throw error;
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Session creation ─────────────────────────────────────────────────

export async function createSession(params: {
  partnerAName: string;
  partnerAAge: AgeRange;
  partnerAAnatomy: Anatomy;
  partnerBName: string;
  partnerBAge: AgeRange;
  partnerBAnatomy: Anatomy;
}): Promise<CreateSessionResponse> {
  return callFunction<CreateSessionResponse>('create-session', { body: params });
}

// ── Answer submission ────────────────────────────────────────────────

interface SubmitAnswersResponse {
  success: boolean;
  partnerSlot: 'A' | 'B';
  alreadySubmitted?: boolean;
  sessionStatus: {
    partnerACompleted: boolean;
    partnerBCompleted: boolean;
    bothCompleted: boolean;
  };
}

export async function submitAnswers(params: {
  sessionId: string;
  partnerToken: string;
  answers: Record<string, AnswerValue>;
}): Promise<SubmitAnswersResponse> {
  return callFunction<SubmitAnswersResponse>('submit-answers', { body: params });
}

// ── Session status (polling) ─────────────────────────────────────────

interface SessionStatusResponse {
  sessionId: string;
  partnerAName: string;
  partnerBName: string;
  partnerAAnatomy: Anatomy;
  partnerBAnatomy: Anatomy;
  partnerACompleted: boolean;
  partnerBCompleted: boolean;
  bothCompleted: boolean;
  isPaid: boolean;
  expiresAt: string;
}

export async function getSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
  return callFunction<SessionStatusResponse>(`session-status?sessionId=${sessionId}`, {
    method: 'GET',
    body: undefined,
  });
}

// ── Match computation ────────────────────────────────────────────────

export async function computeMatches(
  sessionId: string,
  partnerToken: string,
): Promise<MatchResult> {
  return callFunction<MatchResult>(`compute-matches?sessionId=${sessionId}`, {
    method: 'GET',
    partnerToken,
    body: undefined,
  });
}

// ── Stripe checkout ───────────────────────────────────────────────

export async function createCheckout(params: {
  sessionId: string;
  partnerToken: string;
  currency: string;
}): Promise<CreateCheckoutResponse> {
  return callFunction<CreateCheckoutResponse>('create-checkout', { body: params });
}
